// v1.0.0 - 2026-01-31 - File preview component

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface ParsedData {
  type: 'CORRISPETTIVI' | 'MISURE' | 'RIEPILOGO' | 'UNKNOWN'
  rows: number
  columns: string[]
  preview: Record<string, unknown>[]
  errors: string[]
  warnings: string[]
}

interface FilePreviewProps {
  filename: string
  data: ParsedData
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
}

export function FilePreview({ filename, data, onConfirm, onCancel, isLoading }: FilePreviewProps) {
  const typeLabels: Record<string, { label: string; variant: 'default' | 'success' | 'warning' }> = {
    CORRISPETTIVI: { label: 'Corrispettivi GSE', variant: 'success' },
    MISURE: { label: 'Misure Energia', variant: 'success' },
    RIEPILOGO: { label: 'Riepilogo', variant: 'default' },
    UNKNOWN: { label: 'Formato Sconosciuto', variant: 'warning' },
  }

  const { label, variant } = typeLabels[data.type]

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">{filename}</CardTitle>
          <div className="flex gap-2 mt-2">
            <Badge variant={variant}>{label}</Badge>
            <Badge variant="default">{data.rows} righe</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.errors.length > 0 && (
          <div className="rounded-md bg-[var(--color-bg-error)] p-3">
            <p className="font-medium text-[var(--color-error)] mb-1">Errori</p>
            <ul className="list-disc list-inside text-sm text-[var(--color-error)] space-y-1">
              {data.errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {data.warnings.length > 0 && (
          <div className="rounded-md bg-[var(--color-bg-warning)] p-3">
            <p className="font-medium text-[var(--color-warning)] mb-1">Avvisi</p>
            <ul className="list-disc list-inside text-sm text-[var(--color-warning)] space-y-1">
              {data.warnings.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <p className="font-medium mb-2 text-[var(--color-text-primary)]">Anteprima dati</p>
          <div className="rounded-md border border-[var(--color-border)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
                  {data.columns.slice(0, 6).map((col, i) => (
                    <th key={i} className="px-3 py-2 text-left font-medium text-[var(--color-text-primary)]">
                      {col}
                    </th>
                  ))}
                  {data.columns.length > 6 && (
                    <th className="px-3 py-2 text-left font-medium text-[var(--color-text-tertiary)]">
                      +{data.columns.length - 6} colonne
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.preview.slice(0, 5).map((row, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)]">
                    {data.columns.slice(0, 6).map((col, j) => (
                      <td key={j} className="px-3 py-2 truncate max-w-[150px] text-[var(--color-text-secondary)]">
                        {String(row[col] ?? '-')}
                      </td>
                    ))}
                    {data.columns.length > 6 && <td className="px-3 py-2 text-[var(--color-text-tertiary)]">...</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.rows > 5 && (
            <p className="text-xs text-[var(--color-text-tertiary)] mt-2">
              Mostrando 5 di {data.rows} righe
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Annulla
          </Button>
          <Button onClick={onConfirm} disabled={isLoading || data.errors.length > 0}>
            {isLoading ? 'Elaborazione...' : 'Conferma e Processa'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
